<template>
<div class="quote-price-cell">
  <span v-if="quote.status === 'PENDING'" class="quote-pending"><i class="el-icon-loading" /> {{ $t('正在请求中，请稍后') }}</span>
  <template v-else-if="quote.status === 'SUCCESS'">
    <strong class="quote-price">{{ currencySymbol }}{{ quote.customerAmount }}</strong>
    <span class="quote-currency">{{ quote.currency }}</span>
    <span v-if="estimatedCny !== null" class="quote-cny">{{ $t('（约 ¥') }}{{ estimatedCny }}）</span>
    <el-popover placement="left" width="280" trigger="click">
      <div class="quote-detail">
        <strong>{{ $t('费用说明') }}</strong>
        <p>{{ $t('计费重：') }}{{ weightText }}</p>
        <p v-if="residential" class="quote-residential-notice">{{ $t('若为住宅地址，将在试算费用基础上额外收取住宅地址费用，请悉知。') }}</p>
        <p v-if="remoteLabel">{{ $t('偏远类型：') }}{{ $t(remoteLabel) }}</p>
        <template v-if="remarks.length">
          <p>{{ $t('附加项目（仅供参考）：') }}</p>
          <ul><li v-for="(remark, index) in remarks" :key="index">{{ remark }}</li></ul>
        </template>
        <p v-else>{{ $t('暂无附加项目明细') }}</p>
      </div>
      <el-button slot="reference" type="text" icon="el-icon-info" :title="$t('查看费用说明')" class="quote-info" @click.stop />
    </el-popover>
    <el-tooltip v-if="residential" :content="$t('若为住宅地址，将在试算费用基础上额外收取住宅地址费用，请悉知。')" placement="top" :open-delay="150">
      <el-tag size="mini" effect="plain" type="warning" class="quote-surcharge-tag">{{ $t('住宅地址') }}</el-tag>
    </el-tooltip>
    <el-tag v-if="remoteLabel" size="mini" effect="plain" type="warning" class="quote-surcharge-tag">{{ $t(remoteLabel) }}</el-tag>
  </template>
  <template v-else>
    <span class="quote-error">{{ $t(quote.failureReason || '暂无可用报价') }}</span>
    <el-button native-type="button" type="text" icon="el-icon-refresh" class="quote-retry" @click.stop.prevent="$emit('retry', quote)">{{ $t('重试') }}</el-button>
  </template>
</div>
</template>

<script>
import { translateUi } from '@/i18n'
import { approximateCny } from './quoteExchange'

// Provider remarks may be strings, objects or arrays of { code, description }.
function hasResidentialMarker (value) {
  if (value == null) return false
  if (Array.isArray(value)) return value.some(hasResidentialMarker)
  if (typeof value === 'object') return Object.values(value).some(hasResidentialMarker)
  const text = String(value).replace(/\b(?:non[\s_-]*residential|not\s+(?:a\s+)?residential|no\s+residential)\b/gi, '').replace(/非\s*住宅/g, '')
  return /(?:^|[^a-z])residential(?:$|[^a-z])|住宅|(?:^|\D)110920(?:$|\D)/i.test(text)
}

function remarkDescriptions (value) {
  if (value == null) return []
  if (Array.isArray(value)) return value.reduce((items, item) => items.concat(remarkDescriptions(item)), [])
  if (typeof value === 'object') return remarkDescriptions(value.description || value.name || value.remark)
  return typeof value === 'string' && value.trim() ? [value.trim()] : []
}

export default {
  name: 'QuotePrice',
  props: {
    quote: { type: Object, required: true },
    exchangeRate: { type: [Number, String], default: null }
  },
  computed: {
    remarks () {
      for (const value of [this.quote.remark, this.quote.remarks, this.quote.remake, this.quote.reamrk, this.quote.surcharges]) {
        const descriptions = remarkDescriptions(value)
        if (descriptions.length) return descriptions
      }
      return []
    },
    currencySymbol () { return this.quote.currency === 'USD' ? '$' : '' },
    estimatedCny () { return this.quote.currency === 'USD' ? approximateCny(this.quote.customerAmount, this.exchangeRate) : null },
    residential () {
      if (/^(true|1|yes|y)$/i.test(String(this.quote.productResidential).trim())) return true
      const carrier = String(this.quote.carrier || this.quote.category || '').replace(/[\s_-]/g, '').toUpperCase()
      if (!['UPS', 'FEDEX'].includes(carrier)) return false
      return [this.quote.isResidential, this.quote.residential].some(value => /^(true|1|yes|y)$/i.test(String(value).trim())) ||
        [this.quote.residentialFee, this.quote.residentialSurcharge, this.quote.residentialCharge].some(value => Number.isFinite(Number(value)) && Number(value) > 0) ||
        [this.quote.remark, this.quote.remarks, this.quote.remake, this.quote.reamrk, this.quote.surcharges,
          this.quote.surchargeList, this.quote.feeDetails, this.quote.feeDetail,
          this.quote.additionalFeeResultAll, this.quote.additionalFeeResults, this.quote.additionalFees].some(hasResidentialMarker)
    },
    remoteLabel () {
      const evidence = `${this.quote.farawayGrade || ''} ${this.remarks.join(' ')}`
      if (/超超偏远/.test(evidence)) return translateUi('超超偏远')
      if (/超偏远/.test(evidence)) return translateUi('超偏远')
      if (/普通偏远/.test(evidence)) return translateUi('普通偏远')
      if (!this.quote.isFaraway && !/偏远|remote/i.test(evidence)) return ''
      if (/^\s*remote\b/i.test(String(this.quote.farawayGrade || ''))) return translateUi('超超偏远')
      if (/^\s*extended\b/i.test(String(this.quote.farawayGrade || ''))) return translateUi('超偏远')
      return translateUi('普通偏远')
    },
    weightText () {
      if (this.quote.chargeableWeight == null) return '-'
      return `${this.quote.chargeableWeight}${this.quote.weightUnit || ''}`
    }
  }
}
</script>

<style scoped>
.quote-price-cell { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px; min-width: 0; line-height: 1.5; }
.quote-price { color: #e88321; font-weight: 600; font-size: 14px; }
.quote-currency { color: #64716e; font-size: 12px; }
.quote-cny { color: #5f6f79; font-size: 12px; white-space: nowrap; }
.quote-info { padding: 0 2px; color: #e88321; font-size: 16px; }
.quote-price-cell .quote-surcharge-tag { height: 24px; padding: 0 8px; border-color: #e88321; border-radius: 5px; background: #fff; color: #e88321; line-height: 22px; }
.quote-error { color: #e65e5e; white-space: pre-line; overflow-wrap: anywhere; }
.quote-pending { color: #5799e8; }
.quote-retry { padding: 0 2px; }
.quote-detail { color: #3d4b48; font-size: 12px; line-height: 1.5; }
.quote-detail p { margin: 7px 0 0; }
.quote-detail ul { margin: 5px 0 0; padding-left: 18px; }
.quote-detail li { white-space: pre-line; overflow-wrap: anywhere; }
</style>
